import { useEffect, useState } from "preact/hooks";
import { fetchSsmParameters } from "../../api/ssmClient";
import { ENVIRONMENTS } from "../../config";
import { solutionIndex } from "../../state/appState";
import { resolveSolutionNames } from "../../utils/solutionQuery";
import type { Environment, SsmParameter } from "../../types";
import { ParamGroup } from "./ParamGroup";
import { Spinner } from "../../components/Spinner";

type GroupedParams = Record<Environment, Record<string, Record<string, { type: string; value: string }>>>;

function groupParameters(parameters: SsmParameter[]): GroupedParams {
  const grouped: GroupedParams = { DEV: {}, TEST: {}, PROD: {} };
  for (const param of parameters) {
    const parts = param.Name.split("/");
    if (parts.length <= 4) continue;
    const env = parts[3] as Environment;
    if (!(env in grouped)) continue;
    const component = parts[4];
    const paramKey = parts.slice(5).join("/");
    if (!grouped[env][component]) {
      grouped[env][component] = {};
    }
    grouped[env][component][paramKey] = { type: param.Type, value: param.Value };
  }
  return grouped;
}

export function SsmPage({ solution }: { solution: string }) {
  const [grouped, setGrouped] = useState<GroupedParams | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [pickedSolution, setPickedSolution] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const matches = resolveSolutionNames(solution, solutionIndex.value.keys());
  const resolvedSolution =
    matches.length === 1 ? matches[0] : pickedSolution && matches.includes(pickedSolution) ? pickedSolution : null;

  useEffect(() => {
    if (!resolvedSolution) {
      setGrouped(null);
      return;
    }
    let cancelled = false;
    setGrouped(null);
    setError(null);
    fetchSsmParameters(resolvedSolution)
      .then((params) => {
        if (cancelled) return;
        setGrouped(groupParameters(params));
        setFetchedAt(new Date().toISOString());
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching SSM parameters:", err);
        setError("An error occurred. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [resolvedSolution, refreshKey]);

  if (matches.length === 0) {
    return (
      <div class="status-banner">
        <p>No solution matches "{solution}".</p>
      </div>
    );
  }

  if (matches.length > 1 && !resolvedSolution) {
    return (
      <div id="ssmContainer">
        <div class="status-banner">
          <p>Multiple solutions match "{solution}" — pick one to view its SSM parameters:</p>
          <select
            id="ssm-solution-picker"
            value=""
            onChange={(e) => setPickedSolution((e.target as HTMLSelectElement).value)}
          >
            <option value="" disabled>
              Select a solution
            </option>
            {matches.map((match) => (
              <option key={match} value={match}>
                {match}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  if (error) return <p>{error}</p>;
  if (!grouped) return <Spinner label="Loading..." />;

  return (
    <div id="ssmContainer">
      {matches.length > 1 && (
        <div class="status-banner">
          <p>
            Viewing <strong>{resolvedSolution}</strong> ({matches.length} solutions matched "{solution}").{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setPickedSolution(null);
              }}
            >
              Change solution
            </a>
          </p>
        </div>
      )}
      {fetchedAt && (
        <div id="fetchStatus">
          Data fetched: {fetchedAt}{" "}
          <span
            class="refresh-cache"
            title="Refresh SSM parameters for this solution"
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            🔄
          </span>
        </div>
      )}
      <div id="envContainer">
        {ENVIRONMENTS.map((env) => (
          <fieldset key={env}>
            <legend>
              <strong>{env}</strong>
            </legend>
            {Object.keys(grouped[env]).length === 0 && <p>No data...</p>}
            {Object.entries(grouped[env]).map(([component, params]) => (
              <ParamGroup
                key={component}
                solution={resolvedSolution!}
                env={env}
                component={component}
                params={params}
              />
            ))}
          </fieldset>
        ))}
      </div>
    </div>
  );
}
